import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { StarIcon, MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import toast from "react-hot-toast";
import { workspaces } from "../workspace/workspaces";
import { workspaceService } from "../../services/api";
export default function WorkspaceMenu() {
  const [filter, setFilter] = useState("");
  const [busy, setBusy] = useState(false);
  const client = useQueryClient();
  const { data } = useQuery({
    queryKey: ["workspace-preferences"],
    queryFn: workspaceService.preferences,
  });
  const toggle = async (id) => {
    if (!data || busy) return;
    const pins = data.pins.includes(id)
      ? data.pins.filter((p) => p !== id)
      : [...data.pins, id];
    if (pins.length > 6)
      return toast("Pin up to six workspaces. Unpin one to make room.");
    setBusy(true);
    try {
      client.setQueryData(
        ["workspace-preferences"],
        await workspaceService.savePreferences({ ...data, pins }),
      );
    } catch {
      toast.error("Could not save your pinned workspaces.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <main className="unified-page">
      <header className="unified-page-header">
        <div>
          <p className="workspace-eyebrow">Your library</p>
          <h1>Everything has a home.</h1>
          <p>
            Open a workspace beside your assistant. Pin the ones you reach for
            most.
          </p>
        </div>
        <button
          className="secondary-button"
          onClick={() => window.dispatchEvent(new Event("timelyzer:search"))}
        >
          <MagnifyingGlassIcon className="h-4 w-4" />
          Search saved work
        </button>
      </header>
      <label className="workspace-filter">
        Find a workspace
        <input
          className="field-input"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Notes, training, money…"
        />
      </label>
      <nav aria-label="All workspace tools">
        {["Work & planning", "Learning & wellbeing", "Life & family"].map(
          (group) => {
            const matches = workspaces.filter(
              (w) =>
                w.group === group &&
                `${w.label} ${w.description}`
                  .toLowerCase()
                  .includes(filter.toLowerCase()),
            );
            return (
              matches.length > 0 && (
                <section key={group} className="library-group">
                  <h2>{group}</h2>
                  <div className="library-grid">
                    {matches.map(
                      ({ id, to, label, description, icon: Icon }) => (
                        <article className="library-card" key={id}>
                          <Link to={to}>
                            <Icon aria-hidden="true" />
                            <span>
                              <strong>{label}</strong>
                              <span>{description}</span>
                            </span>
                          </Link>
                          <button
                            disabled={busy || !data}
                            className={`pin-button ${data?.pins.includes(id) ? "pinned" : ""}`}
                            aria-label={`${data?.pins.includes(id) ? "Unpin" : "Pin"} ${label}`}
                            aria-pressed={!!data?.pins.includes(id)}
                            onClick={() => toggle(id)}
                          >
                            <StarIcon aria-hidden="true" />
                          </button>
                        </article>
                      ),
                    )}
                  </div>
                </section>
              )
            );
          },
        )}
      </nav>
      {workspaces.every(
        (w) =>
          !`${w.label} ${w.description}`
            .toLowerCase()
            .includes(filter.toLowerCase()),
      ) && (
        <p className="workspace-empty">
          No workspace matches. Search saved work to find a specific record.
        </p>
      )}
      <footer className="library-footer">
        <Link to="/integrations">Connections</Link>
        <Link to="/contexts">Projects & shared context</Link>
        <Link to="/inbox">Unfiled captures</Link>
        <Link to="/social">Public sources</Link>
        <Link to="/preferences">Settings & memory</Link>
        <Link to="/profile">Account</Link>
      </footer>
    </main>
  );
}
